import React, { useEffect, useState } from "react";
import { Sparkles, RefreshCw, Check, X } from "lucide-react";
import { adminApi } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const PRIORITY_STYLES = {
    High: "bg-orchid-500/15 text-orchid-700 border-orchid-500/30",
    Medium: "bg-gold-500/15 text-gold-700 border-gold-500/30",
    Low: "bg-sage-500/15 text-sage-700 border-sage-500/30",
};

export default function Suggestions() {
    const [items, setItems] = useState([]);
    const [generating, setGenerating] = useState(false);

    const load = () => adminApi.get("/api/admin/suggestions").then((r) => setItems(r.data.items));
    useEffect(() => { load(); }, []);

    const generate = async () => {
        setGenerating(true);
        try {
            await adminApi.post("/api/admin/suggestions/generate");
            await load();
        } finally { setGenerating(false); }
    };

    const updateStatus = async (id, status) => {
        await adminApi.patch(`/api/admin/suggestions/${id}`, null, { params: { status } });
        load();
    };

    return (
        <div className="p-10 max-w-6xl">
            <div className="mb-8 flex items-end justify-between">
                <div>
                    <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">AI Insights</div>
                    <h1 className="font-heading text-5xl text-orchid-700">Suggestions</h1>
                    <p className="text-stone-600 mt-2 max-w-2xl">
                        Content gaps Priya has noticed in real guest conversations. Add these topics to your knowledge base to improve responses.
                    </p>
                </div>
                <button onClick={generate} disabled={generating} data-testid="suggestions-generate"
                    className="flex items-center gap-2 bg-orchid-700 hover:bg-orchid-600 text-white text-sm font-medium px-5 py-2.5 rounded-md disabled:opacity-60">
                    {generating ? <RefreshCw size={15} className="animate-spin" /> : <Sparkles size={15} />}
                    {generating ? "Analysing…" : "Generate Suggestions"}
                </button>
            </div>

            {items.length === 0 ? (
                <div className="bg-white border border-stone-200 rounded-lg p-16 text-center">
                    <Sparkles size={32} className="text-orchid-400 mx-auto mb-3" />
                    <div className="font-heading text-2xl text-orchid-700 mb-2">No suggestions yet</div>
                    <p className="text-stone-500 max-w-md mx-auto">
                        Click <strong>Generate Suggestions</strong> — Priya will analyse recent chats and tell you which topics are missing from your knowledge base.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {items.map((s) => (
                        <div key={s.id} data-testid={`suggestion-${s.id}`}
                            className={`bg-white border border-stone-200 rounded-lg p-5 ${s.status !== "open" ? "opacity-60" : ""}`}>
                            <div className="flex items-start justify-between mb-3">
                                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${PRIORITY_STYLES[s.priority] || PRIORITY_STYLES.Medium}`}>
                                    {s.priority} priority
                                </span>
                                {s.category && <span className="text-[10px] uppercase tracking-wider text-stone-500">{s.category}</span>}
                            </div>
                            <div className="font-heading text-xl text-orchid-700 leading-tight">{s.title}</div>
                            <p className="text-sm text-stone-600 mt-2 leading-relaxed">{s.rationale}</p>
                            <div className="flex items-center justify-between mt-4">
                                <div className="text-xs text-stone-400">{formatDateTime(s.created_at)}</div>
                                <div className="flex gap-2">
                                    {s.status === "open" ? (
                                        <>
                                            <button onClick={() => updateStatus(s.id, "addressed")}
                                                className="text-xs flex items-center gap-1 text-sage-700 hover:bg-sage-500/10 px-2 py-1 rounded">
                                                <Check size={13} /> Addressed
                                            </button>
                                            <button onClick={() => updateStatus(s.id, "dismissed")}
                                                className="text-xs flex items-center gap-1 text-stone-500 hover:bg-stone-100 px-2 py-1 rounded">
                                                <X size={13} /> Dismiss
                                            </button>
                                        </>
                                    ) : (
                                        <span className="text-xs text-stone-400 capitalize">{s.status}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
